import { put, takeEvery } from 'redux-saga/effects';
import { ENDPOINT } from '../../services/apiUrls';
import { ResponseGenerator } from './common';
import createApiInstance from '../../services/api';
import { BASE_API_URL } from '../../config';
import {
  captureLead,
  fetchBureauData,
  fetchCreditReport,
  fetchCreditReportByMaskedNumber,
  fetchCreditReportByPan,
  fetchUserDetails,
  revokeConsent,
  saveLeadData,
  updateLoadingState,
  updateUserDetails,
} from '../slices';

// Dynamically create the API instance with the base URL
const api = createApiInstance(BASE_API_URL); // Replace BASE_API_URL with the dynamic value you have

function* fetchCreditScore(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  let url = ENDPOINT.CREDIT_REPORT.getCreditScore.replace(
    '{userNumber}',
    actions.payload.phone
  );

  if (actions.payload.firstName) {
    url += `?firstName=${actions.payload.firstName}`;
  }

  if (actions.payload.lastName) {
    url += `&lastName=${actions.payload.lastName}`;
  }

  if (actions.payload.email) {
    url += `&email=${actions.payload.email}`;
  }

  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: url,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    if (error instanceof Error && 'response' in error) {
      if (actions.payload.callback) {
        actions.payload.callback((error as any).response);
      }
    } else {
      if (actions.payload.callback) {
        actions.payload.callback(false);
      }
    }
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* fetchCreditScoreByPan(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  let url = ENDPOINT.CREDIT_REPORT.getCreditScorePan.replace(
    '{userNumber}',
    actions.payload.phone
  );

  if (actions.payload.firstName) {
    url += `?firstName=${actions.payload.firstName}`;
  }

  if (actions.payload.lastName) {
    url += `&lastName=${actions.payload.lastName}`;
  }

  if (actions.payload.email) {
    url += `&email=${actions.payload.email}`;
  }

  if (actions.payload.pan) {
    url += `&pan=${actions.payload.pan}`;
  }

  if (actions.payload.responseId) {
    url += `&responseId=${actions.payload.responseId}`;
  }

  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* fetchCreditScoreByMaskedNumber(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url:
        ENDPOINT.CREDIT_REPORT.authenticateMaskedMobile.replace(
          '{userNumber}',
          actions.payload.phone
        ) + `?responseId=${actions.payload.responseId}`,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* consentRevoke(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  try {
    const response: ResponseGenerator = yield api.request({
      method: `PATCH`,
      url: `${ENDPOINT.CREDIT_REPORT.consentRevoke.replace('{userNumber}', actions.payload.phone)}`,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* captureLeadGeneration(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  try {
    const response: ResponseGenerator = yield api.request({
      method: `POST`,
      url: `${ENDPOINT.CREDIT_REPORT.leadCapture}`,
      data: actions.payload.data,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* fetchUserCreditDetails(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: `${ENDPOINT.CREDIT_REPORT.creditDetails}/${actions.payload.phone}`,
    });

    actions.payload.callback && actions.payload.callback(response);
    yield put(saveLeadData(response?.data?.leads || []));
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* updateUserCreditDetails(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  try {
    const response: ResponseGenerator = yield api.request({
      method: `PATCH`,
      url: `${ENDPOINT.CREDIT_REPORT.creditDetails}/${actions.payload.phone}`,
      data: actions.payload,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

function* fetchDataByBureau(actions: any) {
  yield put(updateLoadingState({ loaderState: true }));

  try {
    const response: ResponseGenerator = yield api.request({
      method: `GET`,
      url: `${ENDPOINT.CREDIT_REPORT.bureauData.replace('{userNumber}', actions.payload.phone)}`,
    });

    actions.payload.callback && actions.payload.callback(response);
  } catch (error) {
    actions.payload.callback(false);
    console.log(error);
  } finally {
    yield put(updateLoadingState({ loaderState: false }));
  }
}

export default function* root() {
  yield takeEvery(fetchCreditReport.type, fetchCreditScore);
  yield takeEvery(fetchCreditReportByPan.type, fetchCreditScoreByPan);
  yield takeEvery(
    fetchCreditReportByMaskedNumber.type,
    fetchCreditScoreByMaskedNumber
  );
  yield takeEvery(revokeConsent.type, consentRevoke);
  yield takeEvery(captureLead.type, captureLeadGeneration);
  yield takeEvery(fetchUserDetails.type, fetchUserCreditDetails);
  yield takeEvery(updateUserDetails.type, updateUserCreditDetails);
  yield takeEvery(fetchBureauData.type, fetchDataByBureau);
}
